import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Play, RefreshCw, CheckCircle2, XCircle, Clock, Server, Terminal } from 'lucide-react';
import { toast } from 'sonner';

export default function N8nExecutionsMonitor() {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [selectedExec, setSelectedExec] = useState(null);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/openbalancer/n8n-executions');
      const json = await res.json();
      if (json.success && json.data) {
        setExecutions(json.data.executions || []);
      }
    } catch (err) {
      console.error('Failed to fetch n8n executions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutions();
    const interval = setInterval(fetchExecutions, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleTriggerWatchdog = async () => {
    try {
      setTriggering(true);
      const res = await fetch('/api/openbalancer/trigger-watchdog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (json.success) {
        toast.success('n8n Infrastructure Watchdog workflow triggered!');
        setTimeout(fetchExecutions, 2000);
      } else {
        toast.error(json.error || 'Failed to trigger watchdog');
      }
    } catch (err) {
      toast.error('Error triggering watchdog: ' + err.message);
    } finally {
      setTriggering(false);
    }
  };

  const successCount = executions.filter(e => e.status === 'success').length;
  const errorCount = executions.filter(e => e.status === 'error' || e.status === 'crashed').length;

  return (
    <div className="bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-dark-700 pb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-cyan-500/20 border border-cyan-500/30 rounded-xl text-cyan-400">
            <Activity className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <span>n8n Workflow Execution Logs & Infrastructure Watchdog</span>
              <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                Active n8n Node
              </span>
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Live n8n workflow execution history & S.M.A.R.T. infrastructure verification logs
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={fetchExecutions}
            disabled={loading}
            className="px-3 py-1.5 bg-dark-700 hover:bg-dark-600 text-gray-300 rounded-lg text-xs flex items-center gap-1.5 transition-all"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Sync Logs</span>
          </button>

          <button
            onClick={handleTriggerWatchdog}
            disabled={triggering}
            className="px-3.5 py-1.5 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-medium rounded-lg text-xs shadow-md shadow-cyan-500/20 flex items-center gap-1.5 transition-all"
          >
            <Play className="w-3.5 h-3.5" />
            <span>{triggering ? 'Running...' : 'Run Watchdog Now'}</span>
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="bg-dark-900/60 p-3 rounded-xl border border-dark-700/50">
          <span className="text-gray-400">Total Executions</span>
          <p className="text-lg font-bold text-white mt-1">{executions.length}</p>
        </div>
        <div className="bg-dark-900/60 p-3 rounded-xl border border-dark-700/50">
          <span className="text-gray-400">Successful Runs</span>
          <p className="text-lg font-bold text-emerald-400 mt-1">{successCount}</p>
        </div>
        <div className="bg-dark-900/60 p-3 rounded-xl border border-dark-700/50">
          <span className="text-gray-400">Errors / Warnings</span>
          <p className="text-lg font-bold text-rose-400 mt-1">{errorCount}</p>
        </div>
        <div className="bg-dark-900/60 p-3 rounded-xl border border-dark-700/50">
          <span className="text-gray-400">Active Workflow</span>
          <p className="text-sm font-semibold text-cyan-300 mt-1 truncate" title="Infrastructure E2E Verification Watchdog">
            E2E Watchdog
          </p>
        </div>
      </div>

      {/* Execution Logs List */}
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {executions.length === 0 ? (
          <p className="text-xs text-gray-500 italic p-4 text-center">No n8n workflow execution logs fetched yet.</p>
        ) : (
          executions.map((ex) => {
            const isSuccess = ex.status === 'success';
            const isError = ex.status === 'error' || ex.status === 'crashed';

            return (
              <div
                key={ex.id}
                onClick={() => setSelectedExec(ex)}
                className="p-3 bg-dark-900/60 hover:bg-dark-700/50 rounded-xl border border-dark-700/50 flex items-center justify-between text-xs cursor-pointer transition-all"
              >
                <div className="flex items-center space-x-3 truncate">
                  {isSuccess ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : isError ? (
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                  ) : (
                    <Clock className="w-4 h-4 text-cyan-400 shrink-0 animate-spin" />
                  )}

                  <div className="truncate">
                    <div className="font-semibold text-gray-200 flex items-center gap-2">
                      <span className="truncate">{ex.workflowName}</span>
                      <span className="text-[10px] font-mono text-gray-500">#{ex.id}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-2">
                      <span>Mode: <span className="text-gray-300">{ex.mode}</span></span>
                      <span>•</span>
                      <span>Finished: <span className="text-gray-300">{ex.finished ? 'Yes' : 'Running'}</span></span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 shrink-0">
                  <span
                    className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full border ${
                      isSuccess
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : isError
                        ? 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
                    }`}
                  >
                    {ex.status}
                  </span>
                  <span className="text-[10px] font-mono text-gray-500">
                    {new Date(ex.startedAt).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Selected Execution Details Modal */}
      {selectedExec && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 border border-dark-700 p-6 rounded-2xl max-w-xl w-full space-y-4 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-dark-700 pb-3">
              <h4 className="text-sm font-bold text-white flex items-center gap-2">
                <Terminal className="w-4 h-4 text-cyan-400" />
                <span>Execution Details #{selectedExec.id}</span>
              </h4>
              <button
                onClick={() => setSelectedExec(null)}
                className="text-gray-400 hover:text-white text-xs font-bold px-2 py-1 bg-dark-700 rounded-md"
              >
                Close
              </button>
            </div>

            <div className="space-y-2 text-xs text-gray-300 font-mono bg-dark-900 p-4 rounded-xl max-h-60 overflow-y-auto">
              <p><span className="text-cyan-400">Workflow:</span> {selectedExec.workflowName}</p>
              <p><span className="text-cyan-400">Workflow ID:</span> {selectedExec.workflowId}</p>
              <p><span className="text-cyan-400">Status:</span> {selectedExec.status}</p>
              <p><span className="text-cyan-400">Mode:</span> {selectedExec.mode}</p>
              <p><span className="text-cyan-400">Started:</span> {selectedExec.startedAt}</p>
              <p><span className="text-cyan-400">Stopped:</span> {selectedExec.stoppedAt || 'Active'}</p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
